// Boucle musicale synthétisée (WebAudio, zéro asset) — séquenceur à lookahead.
// La mineur, 112 BPM, progression Am F C G. Deux intensités:
//   menu   = basse posée + nappe détunée
//   battle = + basse syncopée, arpèges en doubles-croches, hi-hats
// Respecte le mute global (sound.ts) et ne démarre qu'après un geste utilisateur.

import { isMuted } from './sound'

// Mute séparé pour la musique: on peut couper la boucle en gardant les bruitages.
const KEY_MUSIC_MUTE = 'cryptoket.musicMute'

export function isMusicMuted(): boolean {
  return localStorage.getItem(KEY_MUSIC_MUTE) === '1'
}

export function toggleMusicMute(): boolean {
  const muted = !isMusicMuted()
  localStorage.setItem(KEY_MUSIC_MUTE, muted ? '1' : '0')
  return muted
}

const BPM = 112
const STEP = 60 / BPM / 2 // croches
const STEPS_PER_BAR = 8
const BARS = 4

const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12)
// Am, F, C, G — [fondamentale, tierce, quinte] en midi (octave 3-4)
const CHORDS = [
  [57, 60, 64], // Am
  [53, 57, 60], // F
  [48, 52, 55], // C
  [55, 59, 62], // G
]

type Mode = 'menu' | 'battle'

let ctx: AudioContext | null = null
let master: GainNode | null = null
let timer: number | null = null
let nextTime = 0
let step = 0
let mode: Mode = 'menu'
let started = false

function ensureCtx(): AudioContext | null {
  if (!ctx) {
    try {
      ctx = new AudioContext()
      master = ctx.createGain()
      master.gain.value = 0.045
      master.connect(ctx.destination)
    } catch {
      return null
    }
  }
  if (ctx.state === 'suspended') void ctx.resume()
  return ctx
}

function note(
  freq: number,
  at: number,
  dur: number,
  type: OscillatorType,
  gain: number,
  glideTo?: number,
) {
  if (!ctx || !master) return
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = type
  osc.frequency.setValueAtTime(freq, at)
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, at + dur)
  g.gain.setValueAtTime(0, at)
  g.gain.linearRampToValueAtTime(gain, at + 0.008)
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur)
  osc.connect(g).connect(master)
  osc.start(at)
  osc.stop(at + dur + 0.02)
}

function hat(at: number, accent: boolean) {
  if (!ctx || !master) return
  const len = 0.04
  const buf = ctx.createBuffer(1, ctx.sampleRate * len, ctx.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
  const src = ctx.createBufferSource()
  src.buffer = buf
  const hp = ctx.createBiquadFilter()
  hp.type = 'highpass'
  hp.frequency.value = 7000
  const g = ctx.createGain()
  g.gain.value = accent ? 0.5 : 0.22
  src.connect(hp).connect(g).connect(master)
  src.start(at)
}

function pad(chord: number[], at: number) {
  if (!ctx || !master) return
  const dur = STEP * STEPS_PER_BAR
  for (const m of [chord[0], chord[2]]) {
    for (const detune of [-4, 4]) {
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      const lp = ctx.createBiquadFilter()
      lp.type = 'lowpass'
      lp.frequency.value = 900
      osc.type = 'sawtooth'
      osc.frequency.value = midi(m)
      osc.detune.value = detune
      g.gain.setValueAtTime(0, at)
      g.gain.linearRampToValueAtTime(0.16, at + 0.4)
      g.gain.linearRampToValueAtTime(0.0001, at + dur)
      osc.connect(lp).connect(g).connect(master)
      osc.start(at)
      osc.stop(at + dur + 0.05)
    }
  }
}

function scheduleStep(s: number, at: number) {
  const bar = Math.floor(s / STEPS_PER_BAR) % BARS
  const inBar = s % STEPS_PER_BAR
  const chord = CHORDS[bar]
  const root = chord[0] - 24 // basse octave 1-2

  if (mode === 'menu') {
    if (inBar === 0) {
      note(midi(root), at, STEP * 3, 'triangle', 0.5)
      pad(chord, at)
    }
    if (inBar === 4) note(midi(root), at, STEP * 2, 'triangle', 0.35)
  } else {
    // basse syncopée
    if (inBar === 0 || inBar === 3 || inBar === 6) {
      note(midi(root), at, STEP * 0.9, 'square', 0.4)
    }
    if (inBar === 0) pad(chord, at)
    // arpège en doubles-croches (2 notes par croche)
    const arpTones = [chord[0] + 12, chord[1] + 12, chord[2] + 12, chord[0] + 24]
    note(midi(arpTones[(s * 2) % 4]), at, STEP * 0.42, 'square', 0.14)
    note(midi(arpTones[(s * 2 + 1) % 4]), at + STEP / 2, STEP * 0.42, 'square', 0.14)
    // hats
    hat(at, inBar % 2 === 1)
  }
}

function tick() {
  if (!ctx) return
  // mute global OU mute musique: on saute la planification, la boucle reste en place
  if (isMuted() || isMusicMuted()) {
    nextTime = Math.max(nextTime, ctx.currentTime + 0.1)
    return
  }
  while (nextTime < ctx.currentTime + 0.25) {
    scheduleStep(step, nextTime)
    step = (step + 1) % (STEPS_PER_BAR * BARS)
    nextTime += STEP
  }
}

export const music = {
  /** À appeler après un geste utilisateur (règle d'autoplay). */
  start() {
    if (started) return
    if (!ensureCtx()) return
    started = true
    nextTime = ctx!.currentTime + 0.05
    step = 0
    timer = window.setInterval(tick, 90)
    document.documentElement.dataset.music = 'on'
  },
  setMode(m: Mode) {
    mode = m
  },
  stop() {
    if (timer !== null) clearInterval(timer)
    timer = null
    started = false
    delete document.documentElement.dataset.music
  },
}
